"use client";

import { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"

import flash from "@/lib/flashes"
import { useStableCallback } from "@/tiptap/hooks/use-stable-callback"

function buildInitialTitle({ hasDraft, draftTitle, prename }) {
  return hasDraft ? (draftTitle || prename) : prename
}

function buildInitialContent({ hasDraft, draftContent, precontent }) {
  return hasDraft ? (draftContent || precontent || "") : (precontent || "")
}

function normalizeFolderValue(value) {
  return value == null || value === "" || value === "null" ? "" : value
}

export function useArticleWriter(props) {
  const {
    autosaveUrl,
    closeUrl,
    draftContent,
    draftTitle,
    hasDraft,
    lastSaved,
    lastUpdated,
    precontent,
    prename,
    selectedFolderId,
  } = props

  const [majorError, setMajorError] = useState(false)
  const [htmlContent, setHtmlContent] = useState("")
  const [isDirty, setIsDirty] = useState(false)
  const [restored, setRestored] = useState(hasDraft)
  const [title, setTitle] = useState(() => buildInitialTitle({ hasDraft, draftTitle, prename }))
  const [content, setContent] = useState(() => buildInitialContent({ hasDraft, draftContent, precontent }))
  const [autosavedAt, setAutosavedAt] = useState(lastSaved || lastUpdated || "")
  const [selectedFolder, setSelectedFolder] = useState(() => normalizeFolderValue(selectedFolderId))
  const [editorVersion, setEditorVersion] = useState(0)

  const api = useMemo(() => axios.create({
    headers: {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
    },
  }), [])

  useEffect(() => {
    document.title = title?.length > 0 ? title : "Start writing..."
  }, [title])

  const handleEditorChange = useStableCallback((html, { isUserEdit = true } = {}) => {
    setContent(html)

    if (isUserEdit) {
      setIsDirty(true)
    }
  })

  const save = useStableCallback(async () => {
    try {
      const response = await api.put(autosaveUrl, {
        title,
        content,
        folder_id: normalizeFolderValue(selectedFolder) || null,
      })

      setIsDirty(false)
      setHtmlContent("")
      setMajorError(false)
      setAutosavedAt(response.data.date)
      flash("Draft saved!", "success")
      return true
    } catch {
      setMajorError(true)
      setHtmlContent(content)
      flash("Something went wrong!", "error")
      return false
    }
  })

  useEffect(() => {
    const autosaveTimer = window.setInterval(() => {
      if (isDirty) {
        save()
      }
    }, 120000)

    return () => {
      window.clearInterval(autosaveTimer)
    }
  }, [isDirty, save])

  const publish = useCallback(() => {
    setIsDirty(false)
    document.querySelector("#draftForm")?.submit()
  }, [])

  const restore = useCallback(() => {
    setRestored(true)
    setTitle(draftTitle || prename)
    setContent(draftContent || precontent || "")
    setEditorVersion((current) => current + 1)
    setIsDirty(false)
  }, [draftContent, draftTitle, precontent, prename])

  const unrestore = useCallback(() => {
    if (!window.confirm("The editor will switch to the published version. Your draft will remain saved unless you publish or overwrite it with new draft edits. Continue?")) {
      return
    }

    setRestored(false)
    setTitle(prename)
    setContent(precontent || "")
    setEditorVersion((current) => current + 1)
    setIsDirty(false)
  }, [precontent, prename])

  const handleTitleChange = useCallback((event) => {
    setTitle(event.target.value)
    setIsDirty(true)
  }, [])

  const handleFolderChange = useCallback((event) => {
    setSelectedFolder(normalizeFolderValue(event.target.value))
    setIsDirty(true)
  }, [])

  return {
    autosavedAt,
    closeUrl,
    content,
    editorVersion,
    handleEditorChange,
    handleFolderChange,
    handleTitleChange,
    hasDraft,
    htmlContent,
    isDirty,
    majorError,
    publish,
    restored,
    restore,
    save,
    selectedFolder,
    setHtmlContent,
    setMajorError,
    title,
    unrestore,
  }
}

export default useArticleWriter
